import { COMPANY_FILTER_APPLY, COMPANY_FILTER_CHANGE } from 'containers/PartnerOptions/constants'

import {
  CLEAR_WIZARD_ACTION,
  DISABLE_PSA_INTEGRATION_ASYNC,
  GET_PROVIDER_CONFIG_ASYNC,
  GET_PSA_TILE_ASYNC,
  GET_WIZARD_SCHEMA_ASYNC,
  RESET_PSA_WIZARD_STATE,
  RUN_WIZARD_ACTION_ASYNC,
  SAVE_PSA_INTEGRATION_ASYNC,
} from './constants'

const initialWizardSession = {
  config: null,
  error: null,
  actions: {},
  saveRequestId: null,
  saveLoading: false,
  saveError: null,
  saveResult: null,
}

export const initialPSAWizardState = {
  ...initialWizardSession,
  schemaByProvider: {},
  tiles: {},
  tilesCompany: null,
  disabling: {},
}

const withoutKey = (object, key) =>
  Object.fromEntries(Object.entries(object).filter(([name]) => name !== key))

// A response for an id no longer tracked (cleared or superseded) is dropped, so a slow,
// superseded lookup can never overwrite a newer one.
const updateRequest = (state, requestId, patch) =>
  state.actions[requestId]
    ? {
        ...state,
        actions: { ...state.actions, [requestId]: { ...state.actions[requestId], ...patch } },
      }
    : state

// The save goes on after its wizard closes; its answer must never land in the next session.
const isCurrentSave = (state, requestId) => Boolean(requestId) && requestId === state.saveRequestId

const updateTile = (state, providerKey, patch) =>
  providerKey
    ? {
        ...state,
        tiles: { ...state.tiles, [providerKey]: { ...state.tiles[providerKey], ...patch } },
      }
    : state

const setDisabling = (state, providerKey, value) =>
  providerKey ? { ...state, disabling: { ...state.disabling, [providerKey]: value } } : state

// Tiles and disables belong to one company: a switch clears them, so the previous company's state
// never shows on the new company's tile.
const selectTilesCompany = (state, selectedCompany) => {
  const company = selectedCompany?.value
  if (company === undefined || company === state.tilesCompany) return state
  return { ...state, tiles: {}, disabling: {}, tilesCompany: company }
}

export default function psaWizardReducer(state = initialPSAWizardState, action = {}) {
  switch (action.type) {
    case COMPANY_FILTER_APPLY:
    case COMPANY_FILTER_CHANGE:
      return selectTilesCompany(state, action.payload?.selectedCompany)

    case RESET_PSA_WIZARD_STATE:
      return { ...state, ...initialWizardSession }

    case GET_WIZARD_SCHEMA_ASYNC.SUCCESS:
      return {
        ...state,
        schemaByProvider: action.payload?.providerKey
          ? { ...state.schemaByProvider, [action.payload.providerKey]: action.payload.manifest }
          : state.schemaByProvider,
      }
    case GET_WIZARD_SCHEMA_ASYNC.ERROR:
      return { ...state, error: action.error }

    // INITIATE never clears `error`: the manifest and config load together, and the second
    // start must not wipe the first one's failure. RESET clears it between sessions.
    case GET_PROVIDER_CONFIG_ASYNC.INITIATE:
      return { ...state, config: null }
    case GET_PROVIDER_CONFIG_ASYNC.SUCCESS:
      return { ...state, config: action.payload }
    case GET_PROVIDER_CONFIG_ASYNC.ERROR:
      return { ...state, error: action.error }

    // Only {actionType, status, response, error} is kept: the request payload can carry the
    // plain secret and must never reach the store.
    case RUN_WIZARD_ACTION_ASYNC.INITIATE: {
      const { requestId, actionType } = action.payload || {}
      if (!requestId) return state
      return {
        ...state,
        actions: {
          ...state.actions,
          [requestId]: { actionType, status: 'pending', response: null, error: null },
        },
      }
    }
    case RUN_WIZARD_ACTION_ASYNC.SUCCESS:
      return updateRequest(state, action.payload?.requestId, {
        status: 'success',
        response: action.payload?.response,
      })
    case RUN_WIZARD_ACTION_ASYNC.ERROR:
      return updateRequest(state, action.error?.requestId, {
        status: 'error',
        error: action.error,
      })
    case CLEAR_WIZARD_ACTION:
      return { ...state, actions: withoutKey(state.actions, action.payload?.requestId) }

    case SAVE_PSA_INTEGRATION_ASYNC.INITIATE:
      return {
        ...state,
        saveRequestId: action.payload?.requestId || null,
        saveLoading: true,
        saveError: null,
        saveResult: null,
      }
    case SAVE_PSA_INTEGRATION_ASYNC.SUCCESS:
      if (!isCurrentSave(state, action.payload?.requestId)) return state
      return {
        ...state,
        saveLoading: false,
        saveResult: action.payload.response || { success: true },
      }
    case SAVE_PSA_INTEGRATION_ASYNC.ERROR:
      if (!isCurrentSave(state, action.error?.requestId)) return state
      return { ...state, saveLoading: false, saveError: action.error }

    case GET_PSA_TILE_ASYNC.INITIATE:
      return updateTile(state, action.payload?.providerKey, { loading: true, error: null })
    case GET_PSA_TILE_ASYNC.SUCCESS:
      return updateTile(state, action.payload?.providerKey, {
        loading: false,
        data: action.payload?.config,
      })
    case GET_PSA_TILE_ASYNC.ERROR:
      return updateTile(state, action.error?.providerKey, {
        loading: false,
        error: action.error,
      })

    case DISABLE_PSA_INTEGRATION_ASYNC.INITIATE:
      return updateTile(
        setDisabling(state, action.payload?.providerKey, true),
        action.payload?.providerKey,
        { disableError: null }
      )
    case DISABLE_PSA_INTEGRATION_ASYNC.SUCCESS:
      return setDisabling(state, action.payload?.providerKey, false)
    // Kept apart from the load error: the reload that follows a failed disable clears that one.
    case DISABLE_PSA_INTEGRATION_ASYNC.ERROR:
      return updateTile(
        setDisabling(state, action.error?.providerKey, false),
        action.error?.providerKey,
        { disableError: action.error }
      )

    default:
      return state
  }
}
